document.documentElement.classList.add('zen-reveal-ready');

class ZenHeroSlideshow extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;

    this.controller = new AbortController();
    this.slides = [...this.querySelectorAll('[data-zen-slide]')];
    this.dots = [...this.querySelectorAll('[data-zen-dot]')];
    this.previousButton = this.querySelector('[data-zen-previous]');
    this.nextButton = this.querySelector('[data-zen-next]');
    this.status = this.querySelector('[data-zen-status]');
    this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
    this.timer = null;
    this.preloadTimer = null;
    this.pointerStart = null;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    const options = { signal: this.controller.signal };
    this.addEventListener('click', this.handleClick, options);
    this.addEventListener('mouseenter', this.stop, options);
    this.addEventListener('mouseleave', this.start, options);
    this.addEventListener('focusin', this.stop, options);
    this.addEventListener('focusout', this.start, options);
    this.addEventListener('keydown', this.handleKeydown, options);
    this.addEventListener('pointerdown', this.handlePointerDown, options);
    this.addEventListener('pointerup', this.handlePointerUp, options);
    this.addEventListener('pointercancel', this.clearPointer, options);
    document.addEventListener('visibilitychange', this.handleVisibility, options);
    this.reducedMotion.addEventListener('change', this.handleMotion);

    this.ensureSlideLoaded(this.index);
    this.updateControls();
    this.start();
  }

  disconnectedCallback() {
    this.stop();
    this.controller?.abort();
    this.controller = null;
    this.reducedMotion?.removeEventListener('change', this.handleMotion);
  }

  handleClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('button') : null;
    if (!button || !this.contains(button)) return;
    if (button.matches('[data-zen-previous]')) this.move(-1, true);
    if (button.matches('[data-zen-next]')) this.move(1, true);
    if (button.matches('[data-zen-dot]')) this.show(Number(button.dataset.zenDot), true);
  };

  handleKeydown = (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    this.move(event.key === 'ArrowLeft' ? -1 : 1, true);
  };

  handlePointerDown = (event) => {
    this.pointerStart = event.clientX;
  };

  handlePointerUp = (event) => {
    if (this.pointerStart === null) return;
    const distance = event.clientX - this.pointerStart;
    this.pointerStart = null;
    if (Math.abs(distance) > 48) this.move(distance > 0 ? -1 : 1, true);
  };

  clearPointer = () => {
    this.pointerStart = null;
  };

  handleVisibility = () => {
    if (document.hidden) this.stop();
    else this.start();
  };

  handleMotion = () => {
    if (this.reducedMotion.matches) this.stop();
    else this.start();
  };

  move(direction, manual = false) {
    if (this.slides.length < 2) return;
    this.show((this.index + direction + this.slides.length) % this.slides.length, manual);
  }

  show(nextIndex, manual = false) {
    if (!this.slides[nextIndex] || nextIndex === this.index) {
      if (manual) this.restart();
      return;
    }

    this.ensureSlideLoaded(nextIndex);
    this.slides[this.index].classList.remove('is-active');
    this.slides[this.index].setAttribute('aria-hidden', 'true');
    this.dots[this.index]?.classList.remove('is-active');
    this.dots[this.index]?.setAttribute('aria-selected', 'false');
    this.index = nextIndex;
    this.slides[this.index].classList.add('is-active');
    this.slides[this.index].setAttribute('aria-hidden', 'false');
    this.dots[this.index]?.classList.add('is-active');
    this.dots[this.index]?.setAttribute('aria-selected', 'true');
    this.updateControls();
    if (manual) this.restart();
    else this.start();
  }

  ensureSlideLoaded(index) {
    const slide = this.slides[index];
    if (!slide) return;

    slide.querySelectorAll('[data-zen-lazy-srcset]').forEach((source) => {
      source.srcset = source.dataset.zenLazySrcset;
      source.removeAttribute('data-zen-lazy-srcset');
    });

    slide.querySelectorAll('[data-zen-lazy-slide]').forEach((image) => {
      if (image.dataset.zenLazySrcset) {
        image.srcset = image.dataset.zenLazySrcset;
        image.removeAttribute('data-zen-lazy-srcset');
      }
      if (image.dataset.zenLazySrc) {
        image.src = image.dataset.zenLazySrc;
        image.removeAttribute('data-zen-lazy-src');
      }
      image.removeAttribute('data-zen-lazy-slide');
    });
  }

  updateControls() {
    const total = this.slides.length;
    if (this.status) this.status.textContent = `Slide ${Math.min(this.index + 1, total)} of ${total}`;
    const disabled = total < 2;
    if (this.previousButton) this.previousButton.disabled = disabled;
    if (this.nextButton) this.nextButton.disabled = disabled;
  }

  restart = () => {
    this.stop();
    this.start();
  };

  start = () => {
    if (
      this.timer ||
      this.slides.length < 2 ||
      this.dataset.autoplay !== 'true' ||
      this.reducedMotion.matches ||
      document.hidden ||
      this.matches(':hover') ||
      this.contains(document.activeElement)
    ) return;

    const interval = Math.max(5000, Number(this.dataset.interval) || 6000);
    const nextIndex = (this.index + 1) % this.slides.length;
    this.preloadTimer = window.setTimeout(() => this.ensureSlideLoaded(nextIndex), Math.max(0, interval - 1500));
    this.timer = window.setTimeout(() => {
      this.timer = null;
      this.preloadTimer = null;
      this.move(1);
    }, interval);
  };

  stop = () => {
    if (this.timer) window.clearTimeout(this.timer);
    if (this.preloadTimer) window.clearTimeout(this.preloadTimer);
    this.timer = null;
    this.preloadTimer = null;
  };
}

class ZenHorizontalScroller extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    this.track = this.querySelector('[data-zen-track]');
    this.previousButton = this.querySelector('[data-zen-scroll-previous]');
    this.nextButton = this.querySelector('[data-zen-scroll-next]');
    if (!this.track) return;

    const options = { signal: this.controller.signal };
    this.addEventListener('click', this.handleClick, options);
    this.track.addEventListener('scroll', this.requestUpdate, { passive: true, signal: this.controller.signal });
    this.resizeObserver = new ResizeObserver(this.requestUpdate);
    this.resizeObserver.observe(this.track);
    this.requestUpdate();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    if (this.updateFrame) window.cancelAnimationFrame(this.updateFrame);
    this.updateFrame = null;
  }

  handleClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('button') : null;
    if (!button || !this.contains(button)) return;
    if (button.matches('[data-zen-scroll-previous]')) this.scrollByDirection(-1);
    if (button.matches('[data-zen-scroll-next]')) this.scrollByDirection(1);
  };

  scrollByDirection(direction) {
    const item = this.track.querySelector(':scope > *');
    const amount = item ? item.getBoundingClientRect().width + 18 : this.track.clientWidth;
    this.track.scrollBy({ left: amount * direction, behavior: 'smooth' });
  }

  requestUpdate = () => {
    if (this.updateFrame) return;
    this.updateFrame = window.requestAnimationFrame(() => {
      this.updateFrame = null;
      const maximum = this.track.scrollWidth - this.track.clientWidth;
      if (this.previousButton) this.previousButton.disabled = this.track.scrollLeft <= 2;
      if (this.nextButton) this.nextButton.disabled = this.track.scrollLeft >= maximum - 2;
    });
  };
}

if (!customElements.get('zen-hero-slideshow')) customElements.define('zen-hero-slideshow', ZenHeroSlideshow);
if (!customElements.get('zen-horizontal-scroller')) customElements.define('zen-horizontal-scroller', ZenHorizontalScroller);

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const revealObserver =
  !reducedMotion.matches && 'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-visible');
            revealObserver.unobserve(entry.target);
          });
        },
        { threshold: 0.14, rootMargin: '0px 0px -24px' }
      )
    : null;

function findRevealItems(root) {
  const items = root instanceof Element && root.matches('[data-zen-reveal]') ? [root] : [];
  return items.concat([...root.querySelectorAll('[data-zen-reveal]')]);
}

function prepareReveals(root = document) {
  findRevealItems(root).forEach((item) => {
    if (revealObserver) revealObserver.observe(item);
    else item.classList.add('is-visible');
  });
}

function releaseReveals(root) {
  if (!revealObserver) return;
  findRevealItems(root).forEach((item) => revealObserver.unobserve(item));
}

prepareReveals();
document.addEventListener('shopify:section:load', (event) => prepareReveals(event.target));
document.addEventListener('shopify:section:unload', (event) => releaseReveals(event.target));
